import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, Button, Card, apiErrorMessage, formatMoney, useAuth, brandLocalDate, useBrand } from '@aralere/shared-ui';
import type { ClientInvoiceView, ClientPaymentView, NotatePaymentRequest, PaymentMethod } from '@aralere/shared-types';

// Client "Billing" page (spec §7; Stage-4 slice 4, overlay B). Read-only self-service view of the
// caller's own invoices: OPEN invoices grouped on top (each with a days-remaining due badge, both
// totals per §7.4, and any partial payments so far), COMPLETED invoices grouped under (with the
// full payment history and deep links to any documents the payment unlocked, §18.2a). Both groups
// collapse/expand (owner rule). Everything arrives pre-projected from the server — line cost and
// internal fields never reach this page by construction. Paying happens off-platform for now
// (Zelle, check, money order); card checkout arrives with the payment-processor slice.

const METHOD_LABEL: Record<string, string> = {
  zelle: 'Zelle', check: 'Check', 'money-order': 'Money order', stripe: 'Card (Stripe)', square: 'Card (Square)',
};

/** The reference field's label/placeholder per method (§7.8 — required, method-specific). */
const REF_PLACEHOLDER: Record<string, string> = {
  zelle: 'Zelle reference # *', check: 'Check # *', 'money-order': 'Money order # *',
};

const round2 = (n: number): number => Math.round(n * 100) / 100;


/** YYYY-MM-DD -> MM-DD-YYYY. */
/** 04 #69: a stored rate as a percent label ("2", "2.5"), never a float artefact. */
function pctLabel(rate: number): string {
  return String(Number((rate * 100).toPrecision(12)));
}

function displayDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${m}-${d}-${y}`;
}

/** Whole days from today ON THE BRAND CLOCK (this portal's brand, the client's own; 02 s177) to an ISO
 *  date: 0 = today, negative = past. Both ends are calendar dates, so the arithmetic runs at UTC midnight. */
function daysUntil(iso: string, timezone: string | null): number {
  const today = new Date(`${brandLocalDate(timezone)}T00:00:00Z`).getTime();
  const target = new Date(`${iso}T00:00:00Z`).getTime();
  return Math.round((target - today) / 86_400_000);
}

function dueCountdown(dueDate: string, timezone: string | null): { label: string; overdue: boolean } {
  const d = daysUntil(dueDate, timezone);
  if (d === 0) return { label: 'Due today', overdue: false };
  if (d > 0) return { label: `Due in ${d} day${d === 1 ? '' : 's'}`, overdue: false };
  return { label: `Overdue ${-d} day${d === -1 ? '' : 's'}`, overdue: true };
}

export function ClientBilling(): JSX.Element {
  const { activeClient } = useAuth();
  const { brand } = useBrand();
  const suspended = !!activeClient?.billingSuspended;
  const [open, setOpen] = useState<ClientInvoiceView[]>([]);
  const [completed, setCompleted] = useState<ClientInvoiceView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 04 #98: the server-computed set of open invoices holding a non-payment suspension in place.
  // Never derived client-side from a badge or a due date, so a settled invoice that still renders
  // an "Overdue" badge can never be pinned here.
  const [restoreIds, setRestoreIds] = useState<Set<string>>(new Set());
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({ restore: true, open: true, completed: true });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({}); // per-invoice line detail
  const [searchParams, setSearchParams] = useSearchParams();
  const focusInvoiceId = searchParams.get('invoice');
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const consumedRef = useRef(false);
  const [flashId, setFlashId] = useState<string | null>(null); // brief highlight on the deep-linked card
  // §7.8 client payment notation: which invoice's "report a payment" form is open + its fields.
  const [notateFor, setNotateFor] = useState<string | null>(null);
  const [notateMethod, setNotateMethod] = useState<PaymentMethod>('zelle');
  const [notateAmount, setNotateAmount] = useState('');
  const [notateRef, setNotateRef] = useState('');
  const [notateBusy, setNotateBusy] = useState(false);
  const [notateError, setNotateError] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api.account.listMyBilling();
    setOpen(r.open);
    setCompleted(r.completed);
    setRestoreIds(new Set(r.restoreInvoiceIds ?? []));
  };

  const submitNotation = async (inv: ClientInvoiceView): Promise<void> => {
    if (!notateRef.trim()) {
      setNotateError('A reference is required.');
      return;
    }
    const amt = notateAmount.trim() ? Number(notateAmount) : undefined;
    if (amt !== undefined && (!Number.isFinite(amt) || amt <= 0)) {
      setNotateError('Enter a valid amount, or leave it blank for the remaining balance.');
      return;
    }
    setNotateBusy(true);
    setNotateError(null);
    try {
      const body: NotatePaymentRequest = {
        invoiceId: inv.id,
        method: notateMethod,
        receiptNumber: notateRef.trim(),
        ...(amt !== undefined ? { amount: amt } : {}),
      };
      await api.account.notatePayment(body);
      setNotateFor(null);
      setNotateAmount('');
      setNotateRef('');
      await reload();
    } catch (e) {
      setNotateError(apiErrorMessage(e));
    } finally {
      setNotateBusy(false);
    }
  };

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await api.account.listMyBilling();
        if (!alive) return;
        setOpen(r.open);
        setCompleted(r.completed);
        setRestoreIds(new Set(r.restoreInvoiceIds ?? []));
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Deep link from an invoice notification (linkTo=/billing?invoice=<id>): once loaded, open the
  // target's group, expand its card, scroll it into view, and briefly highlight it. The query param
  // is consume-once (working norm) — cleared after focusing so a refresh / re-nav never replays it.
  useEffect(() => {
    if (!focusInvoiceId || loading || consumedRef.current) return;
    const inOpen = open.some((i) => i.id === focusInvoiceId);
    const inCompleted = completed.some((i) => i.id === focusInvoiceId);
    if (!inOpen && !inCompleted) return;
    consumedRef.current = true;
    // 04 #98: a pinned invoice is still in `open`, but its card renders inside the "restore" group,
    // so the group opened here has to be the one the card actually lives in (04 #98 trap ii: the
    // s79 deep-link contract must survive the reordering).
    const groupKey = restoreIds.has(focusInvoiceId) ? 'restore' : inOpen ? 'open' : 'completed';
    setGroupOpen((m) => ({ ...m, [groupKey]: true }));
    setExpanded((m) => ({ ...m, [focusInvoiceId]: true }));
    setFlashId(focusInvoiceId);
    const raf = requestAnimationFrame(() => {
      cardRefs.current.get(focusInvoiceId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const flash = window.setTimeout(() => setFlashId(null), 2500);
    const next = new URLSearchParams(searchParams);
    next.delete('invoice');
    setSearchParams(next, { replace: true });
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(flash);
    };
  }, [focusInvoiceId, loading, open, completed, restoreIds, searchParams, setSearchParams]);

  /** 04 #72 overlay B: the context line under a payment row. `amount` is already the amount applied
   *  to THIS invoice (s89 overlay A made the whole client surface allocation-aware), so a client
   *  reading the portal against their bank statement sees a smaller figure than they sent. This
   *  says where the rest went. Present only when the server flagged a difference by sending
   *  `receiptTotal`, so an ordinary single-invoice payment gets no extra line at all. */
  const paymentContext = (p: ClientPaymentView): string | null => {
    if (p.receiptTotal === undefined) return null;
    const spread = p.allocationCount ?? 1;
    // 04 #60 WS4: a voluntary tip is why an otherwise ordinary receipt exceeds what was applied.
    const tipTail = (p.tipAmount ?? 0) > 0 ? `; ${formatMoney(p.tipAmount ?? 0)} of it was a tip` : '';
    if (spread > 1) return `part of a ${formatMoney(p.receiptTotal)} payment applied across ${spread} invoices${tipTail}`;
    if (p.excessDisposition === 'refund') return `of a ${formatMoney(p.receiptTotal)} payment; the rest was refunded to you${tipTail}`;
    if (p.excessDisposition === 'credit') return `of a ${formatMoney(p.receiptTotal)} payment; the rest was added to your account as credit${tipTail}`;
    return `of a ${formatMoney(p.receiptTotal)} payment${tipTail}`;
  };

  const renderPayments = (inv: ClientInvoiceView): JSX.Element | null => {
    if (inv.payments.length === 0) return null;
    return (
      <div className="space-y-1 rounded-md bg-[rgb(var(--edge-rgb)/0.03)] p-2">
        <div className="text-xs font-semibold text-[var(--color-text)]">Payments</div>
        {inv.payments.map((p, i) => {
          const context = paymentContext(p);
          return (
            <div key={p.documentNumber ?? i} className="space-y-0.5">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[var(--color-muted)]">
                <span className="font-medium text-[var(--color-text)]">{formatMoney(p.amount)}</span>
                <span>{METHOD_LABEL[p.method] ?? p.method}</span>
                <span>{displayDate(p.dateReceived)}</span>
                {p.documentNumber ? <span>{p.documentNumber}</span> : null}
                {p.receiptNumber ? <span>Ref {p.receiptNumber}</span> : null}
              </div>
              {context ? <div className="text-xs text-[var(--color-muted)]">{context}</div> : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderInvoice = (inv: ClientInvoiceView): JSX.Element => {
    const countdown = inv.state === 'issued' ? dueCountdown(inv.dueDate, brand?.timezone ?? null) : null;
    const showLines = expanded[inv.id] ?? false;
    const pendingTotal = round2(inv.pendingNotations.reduce((s, p) => s + p.amount, 0));
    const remainingToReport = Math.max(
      0,
      round2(inv.totalWithoutConvenienceFee - inv.amountPaid - inv.creditsApplied - pendingTotal),
    );
    return (
      <div
        key={inv.id}
        ref={(el) => {
          if (el) cardRefs.current.set(inv.id, el);
          else cardRefs.current.delete(inv.id);
        }}
        className={`rounded-xl transition ${flashId === inv.id ? 'ring-2 ring-[var(--tint-blue-text)] ring-offset-2' : ''}`}
      >
      <Card>
        <div className="space-y-2 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-[var(--color-text)]">{inv.documentNumber}</span>
              {inv.state === 'paid' ? (
                <span className="rounded-full bg-[var(--tint-green-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tint-green-text)]">Paid</span>
              ) : countdown ? (
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${countdown.overdue ? 'bg-[var(--tint-red-bg)] text-[var(--tint-red-text)]' : 'bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]'}`}>
                  {countdown.label}
                </span>
              ) : null}
              {inv.state === 'issued' && restoreIds.has(inv.id) ? (
                <span className="rounded-full bg-[var(--tint-red-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tint-red-text)]">
                  Services paused
                </span>
              ) : null}
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <button
                type="button"
                className="text-xs text-[var(--color-muted)] underline decoration-dotted underline-offset-2"
                onClick={() => setExpanded((m) => ({ ...m, [inv.id]: !showLines }))}
              >
                {showLines ? 'Hide details' : 'Details'}
              </button>
              {inv.state === 'issued' && remainingToReport > 0 && notateFor !== inv.id ? (
                <Button
                  type="button"
                  size="sm"
                  className="whitespace-nowrap"
                  onClick={() => { setNotateFor(inv.id); setNotateMethod('zelle'); setNotateAmount(''); setNotateRef(''); setNotateError(null); }}
                >
                  Report a payment
                </Button>
              ) : null}
            </div>
          </div>

          <div className="text-xs text-[var(--color-muted)]">
            Issued {displayDate(inv.issueDate)} · Due {displayDate(inv.dueDate)}
            {inv.state === 'paid' && inv.paidAt ? <> · Paid {displayDate(inv.paidAt)}</> : null}
          </div>

          {/* 04 #69 (spec 7.24): the invoice's early-pay terms as frozen at issue. Available or ended is
              the SERVER's verdict on the brand's calendar (02g rule 4'), never this browser's clock; the
              pay-in-full figure is server-computed. Nothing is shown on a settled invoice. */}
          {inv.state === 'issued' && inv.earlyPayDiscount ? (
            inv.earlyPayDiscount.available && inv.earlyPayDiscount.payInFullAmount != null ? (
              <div className="rounded-md border border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] p-2 text-xs text-[var(--tint-green-text)]">
                <span className="font-medium">Early-payment discount:</span>{' '}
                {pctLabel(inv.earlyPayDiscount.rate)}% off through {displayDate(inv.earlyPayDiscount.dueBy)}. Pay{' '}
                <span className="font-semibold">{formatMoney(inv.earlyPayDiscount.payInFullAmount)}</span> by Zelle, check or money order
                to take it{inv.earlyPayDiscount.discountAmount != null && inv.earlyPayDiscount.discountAmount > 0 ? ` (saves ${formatMoney(inv.earlyPayDiscount.discountAmount)})` : ''}.
                {inv.earlyPayDiscount.allowPartial ? ' A partial payment inside the window earns its share.' : ''}
              </div>
            ) : (
              <div className="text-xs text-[var(--color-muted)]">
                Early-payment discount of {pctLabel(inv.earlyPayDiscount.rate)}% ended {displayDate(inv.earlyPayDiscount.dueBy)}.
              </div>
            )
          ) : null}

          {showLines ? (
            <div className="space-y-1 rounded-md bg-[rgb(var(--edge-rgb)/0.03)] p-2 text-xs">
              {inv.lineItems.map((l, i) => (
                <div key={i}>
                  <div className="flex justify-between gap-3">
                    <span>{l.description}{l.qty !== 1 ? ` × ${l.qty}` : ''}</span>
                    <span className="font-medium text-[var(--color-text)]">{formatMoney(l.charge * l.qty)}</span>
                  </div>
                  {/* 04 #160 WS-C M1b: the per-line tax reason the server composed from the frozen record
                      (the same words the PDF and the email print); absent on a fully taxed line. */}
                  {l.taxNote ? <div className="pl-3 text-[11px] text-[var(--color-muted)]">{l.taxNote}</div> : null}
                </div>
              ))}
              <div className="mt-1 flex justify-between border-t border-[rgb(var(--edge-rgb)/0.10)] pt-1"><span>Subtotal</span><span>{formatMoney(inv.subtotal)}</span></div>
              {inv.discountAmount ? <div className="flex justify-between"><span>Discount</span><span>-{formatMoney(inv.discountAmount)}</span></div> : null}
              {/* 04 #160 WS-C M1b (02i §8 DP-7): a reduced-rate group makes a second Tax row, each named by
                  its rate (the server's rows, footing to taxAmount); one unlabelled row otherwise. */}
              {inv.taxLines && inv.taxLines.length > 1 ? (
                inv.taxLines.map((t, i) => (
                  <div key={i} className="flex justify-between"><span>Tax ({pctLabel(t.rate)}%)</span><span>{formatMoney(t.amount)}</span></div>
                ))
              ) : inv.taxAmount !== null ? (
                <div className="flex justify-between"><span>Tax</span><span>{formatMoney(inv.taxAmount)}</span></div>
              ) : null}
              {/* 04 #160 WS-C M1 (02i §8 DP-9): the use-tax footnote the server froze for this invoice. */}
              {inv.taxFootnote ? <div className="text-[11px] text-[var(--color-muted)]">{inv.taxFootnote}</div> : null}
              {Math.abs(inv.totalWithConvenienceFee - inv.totalWithoutConvenienceFee) > 0.005 ? (
                <div className="flex justify-between"><span>Convenience fee</span><span>{formatMoney(inv.convenienceFeeAmount)}</span></div>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-muted)]">
            {Math.abs(inv.totalWithConvenienceFee - inv.totalWithoutConvenienceFee) > 0.005 ? (
              <>
                <span>Total by Cash or Zelle * <span className="font-semibold text-[var(--color-text)]">{formatMoney(inv.totalWithoutConvenienceFee)}</span></span>
                <span>Total (w/convenience fee) <span className="font-semibold text-[var(--color-text)]">{formatMoney(inv.totalWithConvenienceFee)}</span></span>
                <span className="basis-full text-[11px]">* Check or money order are accepted at this same total.</span>
              </>
            ) : (
              <span>Total <span className="font-semibold text-[var(--color-text)]">{formatMoney(inv.totalWithoutConvenienceFee)}</span></span>
            )}
            {inv.amountPaid > 0 ? <span>Paid so far <span className="font-semibold text-[var(--color-text)]">{formatMoney(inv.amountPaid)}</span></span> : null}
            {inv.creditsApplied > 0 ? <span>Credits <span className="font-semibold text-[var(--color-text)]">-{formatMoney(inv.creditsApplied)}</span></span> : null}
            {inv.state === 'issued' ? <span>Balance <span className="font-semibold text-[var(--color-text)]">{formatMoney(inv.balanceDue)}</span></span> : null}
          </div>

          {renderPayments(inv)}

          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs">
            <a
              href={api.account.myInvoiceFileUrl(inv.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-text)] underline decoration-dotted underline-offset-2"
            >
              View PDF
            </a>
            <a
              href={api.account.myInvoiceFileUrl(inv.id, true)}
              className="text-[var(--color-text)] underline decoration-dotted underline-offset-2"
            >
              Download PDF
            </a>
          </div>

          {inv.state === 'issued' && inv.pendingNotations.length > 0 ? (
            <div className="space-y-0.5 rounded-md bg-[var(--tint-warn-bg-soft)] px-2 py-1.5 text-xs text-[var(--tint-warn-text)]">
              {inv.pendingNotations.map((pn, i) => (
                <div key={i}>
                  Payment reported: {formatMoney(pn.amount)} by {METHOD_LABEL[pn.method] ?? pn.method}, awaiting confirmation.
                </div>
              ))}
              {remainingToReport > 0 ? (
                <div>Remaining if approved: <span className="font-semibold">{formatMoney(remainingToReport)}</span></div>
              ) : (
                <div>Your reported payments cover the balance.</div>
              )}
            </div>
          ) : null}

          {inv.state === 'issued' && notateFor === inv.id ? (
            <div className="space-y-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-2 text-xs">
              <div className="font-medium text-[var(--color-text)]">Report a payment you made</div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={notateMethod}
                  onChange={(e) => setNotateMethod(e.target.value as PaymentMethod)}
                  className="rounded border border-[rgb(var(--edge-rgb)/0.15)] bg-[var(--surface-solid)] px-2 py-1"
                >
                  <option value="zelle">Zelle</option>
                  <option value="check">Check</option>
                  <option value="money-order">Money order</option>
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  autoFocus
                  placeholder={`Amount (balance: ${formatMoney(remainingToReport)})`}
                  value={notateAmount}
                  onChange={(e) => setNotateAmount(e.target.value)}
                  className="w-48 rounded border border-[rgb(var(--edge-rgb)/0.15)] bg-[var(--surface-solid)] px-2 py-1"
                />
                <input
                  type="text"
                  placeholder={REF_PLACEHOLDER[notateMethod] ?? 'Reference # *'}
                  value={notateRef}
                  onChange={(e) => setNotateRef(e.target.value)}
                  className="w-52 rounded border border-[rgb(var(--edge-rgb)/0.15)] bg-[var(--surface-solid)] px-2 py-1"
                />
              </div>
              {notateError ? <div className="text-[var(--tint-red-text)]">{notateError}</div> : null}
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={notateBusy}
                  onClick={() => void submitNotation(inv)}
                >
                  {notateBusy ? 'Reporting…' : 'Report payment'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setNotateFor(null); setNotateError(null); }}
                >
                  Cancel
                </Button>
              </div>
              <div className="text-[var(--color-muted)]">
                Pay by Zelle, check, or money order, then report it here. We'll confirm receipt and mark the invoice.
              </div>
            </div>
          ) : null}

          {inv.releasedDocuments.length > 0 ? (
            <div className="text-xs text-[var(--color-muted)]">
              Unlocked documents:{' '}
              {inv.releasedDocuments.map((d, i) => (
                <span key={d.id}>
                  {i > 0 ? ' · ' : ''}
                  <Link className="text-[var(--color-text)] underline underline-offset-2" to={`/documents?doc=${d.id}`}>{d.title}</Link>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </Card>
      </div>
    );
  };

  const renderGroup = (
    key: string,
    title: string,
    rows: ClientInvoiceView[],
    emptyText: string,
    help?: string,
  ): JSX.Element => {
    const isOpen = groupOpen[key] ?? true;
    return (
      <div className="space-y-2">
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-sm font-semibold text-[var(--color-text)] hover:bg-[rgb(var(--edge-rgb)/0.03)]"
          onClick={() => setGroupOpen((m) => ({ ...m, [key]: !isOpen }))}
        >
          <span className="text-xs">{isOpen ? '▾' : '▸'}</span>
          <span>{title}</span>
          <span className="rounded-full bg-[rgb(var(--edge-rgb)/0.06)] px-2 py-0.5 text-xs font-medium text-[var(--color-muted)]">{rows.length}</span>
        </button>
        {isOpen && help ? <div className="px-1 text-xs text-[var(--color-muted)]">{help}</div> : null}
        {isOpen ? (
          rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-[rgb(var(--edge-rgb)/0.10)] p-4 text-center text-xs text-[var(--color-muted)]">{emptyText}</div>
          ) : (
            <div className="space-y-3">{rows.map(renderInvoice)}</div>
          )
        ) : null}
      </div>
    );
  };

  if (loading) return <div className="p-6 text-sm text-[var(--color-muted)]">Loading…</div>;

  // 04 #98: pin the invoices holding the suspension into their own group ABOVE the normal ordering,
  // rather than reordering one list, so a financial list never silently rearranges itself. `open`
  // stays the complete set from the server; the two groups below partition it, so no row is hidden
  // and none is rendered twice. Oldest due first inside the pinned group (the accounting norm and
  // the #64 admin ordering).
  const restoreRows = open
    .filter((i) => restoreIds.has(i.id))
    .slice()
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const openRows = open.filter((i) => !restoreIds.has(i.id));
  const restoreHelp =
    restoreRows.length === 1
      ? 'Your services are paused because this invoice is past due. Pay it to restore them.'
      : 'Your services are paused because these invoices are past due. Pay them to restore your services.';

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 md:p-6">
      <h1 className="text-xl font-semibold text-[var(--color-text)]">Billing</h1>
      {suspended ? (
        <div className="rounded-lg border border-[var(--tint-red-border)] bg-[var(--tint-red-bg-soft)] p-4">
          <div className="text-sm font-semibold text-[var(--tint-red-text)]">Your services are suspended for non-payment</div>
          <div className="mt-1 text-sm text-[var(--tint-red-text)]">
            Your services have been paused because an invoice is past due. Pay your open balance below and
            report the payment; your services reactivate automatically once we confirm it.
          </div>
        </div>
      ) : null}
      {error ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{error}</div> : null}
      {restoreRows.length > 0
        ? renderGroup('restore', 'Pay to restore your services', restoreRows, '', restoreHelp)
        : null}
      {renderGroup('open', 'Open invoices', openRows, 'Nothing due right now.')}
      {renderGroup('completed', 'Paid invoices', completed, 'No paid invoices yet.')}
    </div>
  );
}
